import * as React from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { formatPercentage } from '@/lib/utils'

interface CastEfficiencyProps {
  data: Record<string, {
    casts: number
    maxCasts: number
    efficiency: number
  }>
  title?: string
  description?: string
  className?: string
}

export const CastEfficiency: React.FC<CastEfficiencyProps> = ({
  data,
  title = 'Cast Efficiency',
  description = 'How efficiently abilities were used compared to optimal usage',
  className,
}) => {
  const chartData = Object.entries(data).map(([spell, stats]) => ({
    spell: spell.replace(/([A-Z])/g, ' $1').trim(), // Add spaces before capitals
    efficiency: stats.efficiency * 100,
    casts: stats.casts,
    maxCasts: stats.maxCasts,
    missed: stats.maxCasts - stats.casts,
  })).sort((a, b) => b.efficiency - a.efficiency)

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-md">
          <p className="font-medium mb-2">{label}</p>
          <div className="space-y-1 text-sm">
            <p>
              <span className="text-muted-foreground">Efficiency: </span>
              <span className="font-medium">{formatPercentage(data.efficiency / 100)}</span>
            </p>
            <p>
              <span className="text-muted-foreground">Casts: </span>
              <span className="font-medium">{data.casts} / {data.maxCasts}</span>
            </p>
            {data.missed > 0 && (
              <p>
                <span className="text-muted-foreground">Missed: </span>
                <span className="font-medium text-destructive">{data.missed}</span>
              </p>
            )}
          </div>
        </div>
      )
    }
    return null
  }


  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={chartData}
              layout="horizontal"
              margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                type="number" 
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
                className="text-xs"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                type="category" 
                dataKey="spell"
                className="text-xs"
                tick={{ fontSize: 12 }}
                width={80}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="efficiency"
                fill="hsl(var(--primary))"
                radius={[0, 4, 4, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        <div className="mt-4 flex justify-center space-x-6 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded bg-green-600" />
            <span>≥90% (Excellent)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded bg-yellow-500" />
            <span>≥75% (Good)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded bg-red-500" />
            <span>&lt;75% (Needs Improvement)</span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}